import React, { useState } from "react";
import { useMixture } from "../data/mixture";
import { Input, Label, InputGroup, Button } from "../ui";
import AuthService from "../services/authentication";

function MixtureLineItems({ mixture }) {
  const auth = AuthService.useAuth();
  const { updateLineItem, deleteLineItem } = useMixture(
    mixture.id,
    auth.authHeader()
  );
  const [markups, setMarkups] = useState({});
  const [blankQuantities, setBlankQuantities] = useState(new Set());

  const handleLineItemChange = (lineItem, key, value) => {
    updateLineItem(lineItem.id, key, value);
  };

  const handleQuantityChange = (lineItem, quantity, allow_zero = false) => {
    if (quantity === "") {
      setBlankQuantities(prev => new Set(prev).add(lineItem.id));
    } else {
      setBlankQuantities(prev => {
        const newSet = new Set(prev);
        newSet.delete(lineItem.id);
        return newSet;
      });
      if (quantity <= mixture.max_weight || allow_zero) {
        handleLineItemChange(lineItem, "quantity", parseInt(quantity));
      }
    }
  };

  const handleRemoveLineItem = (lineItem) => {
    deleteLineItem(lineItem.id);
  };

  const handleMarkupChange = (lineItem, value) => {
    if (value !== "") {
      value = Math.max(0, Math.min(300, value));
    }
    setMarkups((prevMarkups) => ({ ...prevMarkups, [lineItem.id]: value }));
    if (value !== "" && value >= 0 && value <= 300) {
      handleLineItemChange(lineItem, "markup", value);
    }
  };

  const renderMarkupForm = (lineItem) => {
    const markup = markups[lineItem.id] ?? lineItem.markup ?? "";

    return (
      <InputGroup
        className="mt-2"
        style={lineItem.use_rrp ? { opacity: 0.2, pointerEvents: "none" } : {}}
      >
        <Label className="w-full text-gray-500 mb-1">Markup:</Label>
        <Input
          name="markup"
          type="number"
          style={{ maxWidth: "7em" }}
          placeholder="0"
          onChange={(e) => handleMarkupChange(lineItem, e.target.value)}
          value={markup}
          min={0}
          step={0.01}
        />
        <InputGroup.Text>%</InputGroup.Text>
      </InputGroup>
    );
  };

  const quantityForm = (lineItem) => {
    return (
      <form className="inline-flex items-center">
        <InputGroup>
          <Label className="w-full text-gray-500 mb-1">Quantity:</Label>
          <Input
            style={{ maxWidth: "7em" }}
            type="number"
            value={blankQuantities.has(lineItem.id) ? "" : (lineItem.quantity || "")}
            onBlur={(e) =>
              handleQuantityChange(lineItem, e.target.value, false)
            }
            onChange={(e) =>
              handleQuantityChange(lineItem, e.target.value, true)
            }
          />
          <InputGroup.Text>ml</InputGroup.Text>
          <Button
            variant="danger"
            onClick={(e) => handleRemoveLineItem(lineItem)}
          >
            Remove
          </Button>
        </InputGroup>
      </form>
    );
  };

  const renderPricingOptions = (lineItem) => {
    return (
      <form className="mt-2">
        <Label className="text-gray-500 me-2 mb-0">Pricing:</Label>
        <div className="flex items-center gap-2">
          <input
            id={`rrp-${lineItem.id}`}
            name={`pricing-${lineItem.id}`}
            onChange={(e) =>
              handleLineItemChange(lineItem, "use_rrp", !lineItem.use_rrp)
            }
            type="radio"
            checked={lineItem.use_rrp}
          />
          <Label htmlFor={`rrp-${lineItem.id}`} className="mb-0">RRP</Label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id={`markup-${lineItem.id}`}
            name={`pricing-${lineItem.id}`}
            onChange={(e) =>
              handleLineItemChange(lineItem, "use_rrp", !lineItem.use_rrp)
            }
            type="radio"
            checked={!lineItem.use_rrp}
          />
          <Label htmlFor={`markup-${lineItem.id}`} className="mb-0">Markup</Label>
        </div>
      </form>
    );
  };

  return (
    <ul className="divide-y divide-gray-200 px-4">
      {mixture.sales_order_items.map((lineItem) => (
        <li key={lineItem.id} className="py-2 mt-1">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-9">
              <p className="w-full font-bold">{lineItem.product.name}</p>
              <div>
                {quantityForm(lineItem)}
                {renderPricingOptions(lineItem)}
                {renderMarkupForm(lineItem)}
              </div>
            </div>
            <div className="col-span-3 flex content-end justify-end">
              <p className="font-bold">
                ${(lineItem.patient_price * lineItem.quantity).toFixed(2)}
              </p>
            </div>
          </div>
        </li>
      ))}
      <li key="dispensing-charge" className="py-2 mt-1">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-9">
            <p className="w-full mb-0">Dispensing Charge</p>
          </div>
          <div className="col-span-3 flex content-end justify-end">
            <p className="mb-0">$9.77</p>
          </div>
        </div>
      </li>
    </ul>
  );
}

export default MixtureLineItems;
